import {
  computed,
  ref,
  watch,
  ComputedRef,
  Ref,
  onBeforeUnmount,
  useTemplateRef,
  ComponentPublicInstance,
} from 'vue';
import { FilterFn, getFilteredRowModel } from '@tanstack/vue-table';
import { debounce, isEqual, merge } from 'lodash-es';
import { useModernGridFilterSearch } from '@/common/components/molecules/filterSearch/filterSearch.uses';
import { useAutoValueItems } from '@/common/composables/useAutoValueItems';
import type { Emits, Props } from '../../modernGrid.setup';
import type {
  GridRowDataType,
  GridRawRowDataType,
  GridTableInstance,
  GridTableInstanceOptions,
  GridRowInstance,
  GridColumnInstance,
  GridDataFilter,
  TextFilterOption,
  TokenFilterOption,
  GridTreeViewOption,
} from '../../modernGrid.types';
import { defaultTextFilter, defaultTokenFilter } from '../../modernGrid.define';
import { checkNumberStr, matchArray, matchStringValue } from '../../modernGrid.utils';
import { FilterSearchExposed } from '../../../filterSearch/filterSearch.type';

export const useFilter = <RowDataType extends GridRawRowDataType>({
  props,
  emit,
  table,
  currentPageNum,
  tableColumns,
  treeViewInfo,
}: {
  props: Props<RowDataType>;
  emit: Emits<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  currentPageNum: Ref<number>;
  tableColumns: ComputedRef<GridColumnInstance<RowDataType>[]>;
  treeViewInfo: ComputedRef<GridTreeViewOption>;
}) => {
  const filterSearchRef = useTemplateRef<ComponentPublicInstance & FilterSearchExposed>(
    'filterSearchRef',
  );
  const textFilterInfo = computed<TextFilterOption>(() =>
    merge({}, defaultTextFilter, props.option?.textFilter),
  );
  const searchWord = ref<string>(props.initialSearchWord ?? '');

  const tokenFilterInfo = computed<TokenFilterOption>(() =>
    merge({}, defaultTokenFilter, props.option?.tokenFilter),
  );
  const useServerSideTokenFilter = computed(
    () => tokenFilterInfo.value.use && !tokenFilterInfo.value.useClient,
  );
  const useServerSideTextFilter = computed(
    () => textFilterInfo.value.use && !textFilterInfo.value.useClient,
  );
  const filterableColumns = computed<GridColumnInstance<RowDataType>[]>(() =>
    tableColumns.value.filter((column) => column.columnDef.enableGlobalFilter === true),
  );
  const { filterSearchResultMV, filterGridByFilterSearch, quickFilterResultMV } =
    useModernGridFilterSearch<RowDataType>({
      columns: filterableColumns as ComputedRef<GridColumnInstance<RowDataType>[]>,
      filterItems: tokenFilterInfo.value.filterItems || [],
    });
  const globalFilter = ref<GridDataFilter>();
  const filterCaches = ref<Map<string, boolean>>(new Map());

  let oldFilter: GridDataFilter | undefined;

  const searchString = (
    row: GridRowInstance<RowDataType>,
    columnId: string,
    filterValue: string | undefined,
  ): boolean => {
    const column = filterableColumns.value?.find((col) => col.id === columnId);
    if (!filterValue || !column || !column.getIsVisible()) return true;

    const rawValue = row.original[columnId];
    const formattedValue = row.getValue(columnId) || rawValue;
    if (formattedValue == null) return false;

    const { rendererType, type: dataType, filterType, formatter } = column.columnDef.meta || {};
    const hasFormatter = !!formatter && !!filterType;
    const keyword = filterValue.toLowerCase();
    let isRawMatched = false;
    let isFormattedMatched = false;

    if (
      rendererType === 'chip-cell' ||
      rendererType === 'chip-cell-clickable' ||
      rendererType === 'color-ball-cell'
    ) {
      isFormattedMatched = matchArray(formattedValue, keyword);
      if (hasFormatter) {
        isRawMatched = matchArray(rawValue, keyword);
      }
    } else if (rendererType === 'multi-chip-cell') {
      isFormattedMatched =
        typeof formattedValue === 'object' && matchArray(Object.keys(formattedValue), keyword);
      if (hasFormatter) {
        isRawMatched = typeof rawValue === 'object' && matchArray(Object.keys(rawValue), keyword);
      }
    } else if (dataType === 'number' && checkNumberStr(keyword)) {
      const formattedNum = +formattedValue;
      isFormattedMatched = Number.isNaN(formattedNum)
        ? matchStringValue(formattedValue, keyword)
        : formattedNum === +keyword;
      if (hasFormatter && checkNumberStr(rawValue)) {
        isRawMatched = +rawValue === +keyword;
      }
    } else {
      isFormattedMatched = matchStringValue(
        typeof formattedValue === 'string' ? formattedValue : `${formattedValue}`,
        keyword,
      );
      if (hasFormatter) {
        isRawMatched = matchStringValue(
          typeof rawValue === 'string' ? rawValue : `${rawValue}`,
          keyword,
        );
      }
    }

    let isMatched = isFormattedMatched;
    if (filterType === 'onlyRaw') {
      isMatched = isRawMatched;
    } else if (filterType === 'both') {
      isMatched = isRawMatched || isFormattedMatched;
    }
    return isMatched;
  };

  // row 자체의 필터 매칭 여부 계산 (부모/자식 관계 무시)
  const checkRowMatchesFilter = (
    row: GridRowInstance<RowDataType>,
    filterValue: GridDataFilter,
  ): boolean => {
    let passesTextFilter = true;
    let passesTokenFilter = true;
    let passesQuickFilter = true;

    if (textFilterInfo.value.use && filterValue.textFilter) {
      passesTextFilter = filterableColumns.value.some((column) =>
        searchString(row, column.id, filterValue.textFilter),
      );
    }

    if (tokenFilterInfo.value.use) {
      passesTokenFilter = filterGridByFilterSearch(
        row,
        filterValue.tokenFilter,
        tokenFilterInfo.value.customOption,
      );
    }

    if (tokenFilterInfo.value.quickFilter?.use) {
      passesQuickFilter = filterGridByFilterSearch(
        row,
        filterValue.quickFilter,
        tokenFilterInfo.value.customOption,
      );
    }

    return passesTextFilter && passesTokenFilter && passesQuickFilter;
  };

  // 부모 row 중 필터에 매칭되는 row가 있는지 확인
  const hasMatchingAncestor = (
    row: GridRowInstance<RowDataType>,
    filterValue: GridDataFilter,
  ): boolean => {
    let parentRow = row.getParentRow();
    while (parentRow) {
      const parentKey = `${parentRow.id}-${JSON.stringify(filterValue)}`;
      if (filterCaches.value.has(parentKey)) {
        if (filterCaches.value.get(parentKey) === true) {
          return true;
        }
      } else {
        const parentMatches = checkRowMatchesFilter(parentRow, filterValue);
        filterCaches.value.set(parentKey, parentMatches);
        if (parentMatches) {
          return true;
        }
      }
      parentRow = parentRow.getParentRow();
    }
    return false;
  };

  const complexGlobalFilter: FilterFn<GridRowDataType<RowDataType>> = (
    row: GridRowInstance<RowDataType>,
    columnId: string,
    filterValue: GridDataFilter,
  ): boolean => {
    if (
      !filterValue ||
      (!oldFilter &&
        !filterValue.textFilter &&
        !filterValue.tokenFilter?.tokens?.length &&
        !filterValue.quickFilter?.tokens?.length)
    ) {
      return true;
    }

    oldFilter = filterValue;
    const rowKey = `${row.id}-${JSON.stringify(filterValue)}`;

    if (filterCaches.value.has(rowKey)) {
      return filterCaches.value.get(rowKey)!;
    }

    // 트리뷰에서 부모가 필터에 매칭되면 자식도 표시
    if (treeViewInfo.value?.use && row.depth > 0) {
      if (hasMatchingAncestor(row, filterValue)) {
        filterCaches.value.set(rowKey, true);
        return true;
      }
    }

    // FilterSearch & QuickFilter 'AND' 조건 (&&)
    const result = checkRowMatchesFilter(row, filterValue);
    filterCaches.value.set(rowKey, result);
    return result;
  };

  const updateFilter = (updater: GridDataFilter | ((state: GridDataFilter) => GridDataFilter)) => {
    globalFilter.value =
      typeof updater === 'function'
        ? updater(globalFilter.value || table.value?.getState().globalFilter)
        : updater;
    currentPageNum.value = 1;

    emit('change-filter', {
      textFilter: searchWord.value,
      tokenFilter: filterSearchResultMV.value,
    });
  };

  const filterOptions = computed<Partial<GridTableInstanceOptions<RowDataType>>>(() => ({
    getFilteredRowModel: getFilteredRowModel(),
    manualFiltering:
      (!textFilterInfo.value.use && !tokenFilterInfo.value.use) ||
      useServerSideTokenFilter.value ||
      useServerSideTextFilter.value,
    globalFilterFn: complexGlobalFilter,
    onGlobalFilterChange: updateFilter,
  }));

  watch(
    () => filterSearchResultMV.value,
    (newVal, oldVal) => {
      if (isEqual(newVal, oldVal) || (!oldVal && !newVal?.tokens.length)) return;
      table.value?.setGlobalFilter({
        textFilter: searchWord.value,
        tokenFilter: newVal,
        quickFilter: quickFilterResultMV.value,
      });
    },
  );

  watch(
    () => quickFilterResultMV.value,
    (newVal, oldVal) => {
      if (isEqual(newVal, oldVal) || (!oldVal && !newVal?.tokens.length)) return;
      table.value?.setGlobalFilter({
        textFilter: searchWord.value,
        tokenFilter: filterSearchResultMV.value,
        quickFilter: newVal,
      });
    },
  );

  const debouncedSetGlobalFilter = debounce((val) => {
    table.value?.setGlobalFilter({
      textFilter: val,
      tokenFilter: filterSearchResultMV.value,
      quickFilter: quickFilterResultMV.value,
    });
  }, 200);

  watch(
    () => searchWord.value,
    (newVal, oldVal) => {
      if (newVal === oldVal) return;
      emit('update:search-word', newVal);
      debouncedSetGlobalFilter(newVal);
    },
  );

  watch(
    () => props.initialSearchWord,
    (newVal) => {
      if (newVal !== undefined && newVal !== searchWord.value) {
        searchWord.value = newVal;
      }
    },
  );

  watch(
    () => globalFilter.value,
    () => {
      filterCaches.value.clear();
    },
    { deep: true },
  );

  onBeforeUnmount(() => {
    debouncedSetGlobalFilter.cancel();
  });

  const isAutoValueEnabled = computed(
    () => tokenFilterInfo.value.useAutoValueItems !== false && tokenFilterInfo.value.use,
  );
  const _autoValueItems = useAutoValueItems({
    rows: computed(() => (isAutoValueEnabled.value ? (props.rows ?? []) : [])),
    filterItems: computed(() =>
      isAutoValueEnabled.value ? (tokenFilterInfo.value.filterItems ?? []) : [],
    ),
    childrenKey: computed(() => (treeViewInfo.value?.use ? treeViewInfo.value.subRowKey : '')),
  });
  const autoValueItems = computed(() => (isAutoValueEnabled.value ? _autoValueItems.value : {}));

  const clearFilterSearchResultMV = () => {
    filterSearchResultMV.value = undefined;
  };

  const clearFilterSearchToken = () => {
    clearFilterSearchResultMV();
    filterSearchRef.value?.clearFilterSearchToken?.();
  };

  return {
    filterSearchRef,
    textFilterInfo,
    tokenFilterInfo,
    filterSearchResultMV,
    quickFilterResultMV,
    searchWord,
    filterOptions,
    globalFilter,
    filterCaches,
    autoValueItems,
    clearFilterSearchResultMV,
    clearFilterSearchToken,
  };
};
